// DealershipQuotePage.js
import React, { useState } from 'react';
import { Building, Users, Calendar, Mail, Phone, Clock, MapPin, ChevronRight, ArrowRight, Check } from 'lucide-react';
import emailService from '../services/emailService';

const DealershipQuotePage = ({ isMobile }) => {
  const [formData, setFormData] = useState({
    dealershipName: '',
    contactName: '',
    email: '',
    phone: '',
    inventorySize: '',
    vehicleTypes: '',
    address: '',
    preferredSchedule: '',
    serviceNeeds: [],
    additionalInfo: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    const serviceNeeds = [...formData.serviceNeeds];
    
    if (checked) {
      serviceNeeds.push(name);
    } else {
      const index = serviceNeeds.indexOf(name);
      if (index > -1) {
        serviceNeeds.splice(index, 1);
      }
    }
    
    setFormData({
      ...formData,
      serviceNeeds
    });
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.dealershipName.trim()) newErrors.dealershipName = "Dealership name is required";
    if (!formData.contactName.trim()) newErrors.contactName = "Contact name is required";
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    
    // Phone validation (optional but if provided should be valid)
    if (formData.phone && !/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = "Phone number should be 10 digits";
    }
    
    if (!formData.inventorySize.trim()) newErrors.inventorySize = "Inventory size is required";
    if (!formData.address.trim()) newErrors.address = "Dealership address is required";
    if (formData.serviceNeeds.length === 0) newErrors.serviceNeeds = "Please select at least one service need";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        // Send form data to bookings@jp-autodetailing.com to trigger auto-responder
        await emailService.sendFormToBookings('dealership', formData, 'dealership-quote-page');

        // Show success message
        setIsSubmitted(true);

      } catch (error) {
        console.error("Error submitting form:", error);
        alert("There was an error submitting your quote request. Please try again.");
      }
    } else {
      console.log("Form has errors");
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 pt-16 md:pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-cyan-500 py-12 md:py-16 px-4 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-white" 
               style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>
        
        <div className="max-w-4xl mx-auto relative">
          <div className="text-center text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-4">Dealership Detailing Quote</h1>
            <div className="w-20 h-1 bg-white/30 mx-auto mb-6"></div>
            <p className="text-white/90 max-w-xl mx-auto">
              Complete the form below to receive a customized quote for your dealership detailing needs.
              We'll contact you within 24 hours to discuss your requirements in detail.
            </p>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-12 md:py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Form Column */}
            <div className="lg:col-span-8">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                {isSubmitted ? (
                  // Success message
                  <div className="p-8 md:p-12 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Check className="w-8 h-8 text-green-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Request Submitted!</h2>
                    <p className="text-gray-600 mb-6">
                      Thank you for your interest in our dealership detailing services. We've received your request and will contact you within 24 hours to discuss your customized quote.
                    </p>
                    <p className="text-gray-600 mb-6">
                      A confirmation has been sent to your email: <strong>{formData.email}</strong>
                    </p>
                    <button 
                      onClick={() => window.history.back()}
                      className="inline-flex items-center px-6 py-3 bg-blue-500 text-white rounded-lg font-medium transition-all hover:bg-blue-600"
                    >
                      <ChevronRight className="mr-2 h-5 w-5" />
                      Return to Previous Page
                    </button>
                  </div>
                ) : (
                  // Quote request form
                  <form onSubmit={handleSubmit} className="p-6 md:p-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">Dealership Information</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {/* Dealership Name */}
                      <div>
                        <label htmlFor="dealershipName" className="block text-sm font-medium text-gray-700 mb-1">
                          Dealership Name*
                        </label>
                        <input
                          type="text"
                          id="dealershipName"
                          name="dealershipName"
                          value={formData.dealershipName}
                          onChange={handleChange}
                          className={`w-full px-4 py-2 border ${errors.dealershipName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
                          placeholder="Your dealership name"
                        />
                        {errors.dealershipName && <p className="mt-1 text-sm text-red-500">{errors.dealershipName}</p>}
                      </div>
                      
                      {/* Contact Name */}
                      <div>
                        <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-1">
                          Contact Name*
                        </label>
                        <input
                          type="text"
                          id="contactName"
                          name="contactName"
                          value={formData.contactName}
                          onChange={handleChange}
                          className={`w-full px-4 py-2 border ${errors.contactName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
                          placeholder="Primary contact person"
                        />
                        {errors.contactName && <p className="mt-1 text-sm text-red-500">{errors.contactName}</p>}
                      </div>
                      
                      {/* Email */}
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address*
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full px-4 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
                          placeholder="you@dealership.com"
                        />
                        {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                      </div>
                      
                      {/* Phone */}
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number*
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={`w-full px-4 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
                          placeholder="(123) 456-7890"
                        />
                        {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
                      </div>
                    </div>
                    
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">Service Requirements</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {/* Inventory Size */}
                      <div>
                        <label htmlFor="inventorySize" className="block text-sm font-medium text-gray-700 mb-1">
                          Inventory Size*
                        </label>
                        <input
                          type="text"
                          id="inventorySize"
                          name="inventorySize"
                          value={formData.inventorySize}
                          onChange={handleChange}
                          className={`w-full px-4 py-2 border ${errors.inventorySize ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
                          placeholder="Number of vehicles"
                        />
                        {errors.inventorySize && <p className="mt-1 text-sm text-red-500">{errors.inventorySize}</p>}
                      </div>
                      
                      {/* Vehicle Types */}
                      <div>
                        <label htmlFor="vehicleTypes" className="block text-sm font-medium text-gray-700 mb-1">
                          Vehicle Types
                        </label>
                        <input
                          type="text"
                          id="vehicleTypes"
                          name="vehicleTypes"
                          value={formData.vehicleTypes}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                          placeholder="Luxury, SUVs, sedans, etc."
                        />
                      </div>
                      
                      {/* Preferred Schedule */}
                      <div className="md:col-span-2">
                        <label htmlFor="preferredSchedule" className="block text-sm font-medium text-gray-700 mb-1">
                          Preferred Schedule
                        </label>
                        <input
                          type="text"
                          id="preferredSchedule"
                          name="preferredSchedule"
                          value={formData.preferredSchedule}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                          placeholder="Specific days or times that work best"
                        />
                      </div>
                    </div>
                    
                    {/* Service Needs Checkboxes */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Service Needs* (Select all that apply)
                      </label>
                      
                      <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${errors.serviceNeeds ? 'border border-red-500 p-3 rounded-lg' : ''}`}>
                        <label className="flex items-center">
                          <input 
                            type="checkbox" 
                            name="new_inventory"
                            className="mr-2"
                            checked={formData.serviceNeeds.includes('new_inventory')}
                            onChange={handleCheckboxChange}
                          />
                          <span className="text-sm text-gray-700">New Inventory Preparation</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input 
                            type="checkbox" 
                            name="used_reconditioning"
                            className="mr-2"
                            checked={formData.serviceNeeds.includes('used_reconditioning')}
                            onChange={handleCheckboxChange}
                          />
                          <span className="text-sm text-gray-700">Used Vehicle Reconditioning</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input 
                            type="checkbox" 
                            name="auction_prep"
                            className="mr-2"
                            checked={formData.serviceNeeds.includes('auction_prep')}
                            onChange={handleCheckboxChange}
                          />
                          <span className="text-sm text-gray-700">Auction Preparation</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input 
                            type="checkbox" 
                            name="display_vehicles"
                            className="mr-2"
                            checked={formData.serviceNeeds.includes('display_vehicles')}
                            onChange={handleCheckboxChange}
                          />
                          <span className="text-sm text-gray-700">Showroom/Display Vehicles</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input 
                            type="checkbox" 
                            name="ceramic_coating"
                            className="mr-2"
                            checked={formData.serviceNeeds.includes('ceramic_coating')}
                            onChange={handleCheckboxChange}
                          />
                          <span className="text-sm text-gray-700">Ceramic Coating</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input 
                            type="checkbox" 
                            name="paint_correction"
                            className="mr-2"
                            checked={formData.serviceNeeds.includes('paint_correction')}
                            onChange={handleCheckboxChange}
                          />
                          <span className="text-sm text-gray-700">Paint Correction</span>
                        </label>
                      </div>
                      {errors.serviceNeeds && <p className="mt-1 text-sm text-red-500">{errors.serviceNeeds}</p>}
                    </div>
                    
                    {/* Dealership Address */}
                    <div className="mb-6">
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                        Dealership Address*
                      </label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border ${errors.address ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
                        placeholder="Full dealership address"
                      />
                      {errors.address && <p className="mt-1 text-sm text-red-500">{errors.address}</p>}
                    </div>
                    
                    {/* Additional Information */}
                    <div className="mb-8">
                      <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Information
                      </label>
                      <textarea
                        id="additionalInfo"
                        name="additionalInfo"
                        value={formData.additionalInfo}
                        onChange={handleChange}
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                        placeholder="Any specific requirements or questions..."
                      ></textarea>
                    </div>
                    
                    {/* Submit button */}
                    <button
                      type="submit"
                      className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-400 text-white py-3 px-6 rounded-lg font-medium hover:from-blue-600 hover:to-cyan-500 transition-all duration-300 shadow-md hover:shadow-lg"
                    >
                      <ArrowRight className="h-5 w-5" />
                      <span>Submit Quote Request</span>
                    </button>
                  </form>
                )}
              </div>
            </div>
            
            {/* Info Column */}
            <div className="lg:col-span-4">
              <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-6">Why Choose Our Dealership Services</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Building className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">Dealership Specialists</h4>
                      <p className="text-gray-600 text-sm">Experienced with dealership requirements</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">Volume Capacity</h4>
                      <p className="text-gray-600 text-sm">Equipped to handle large inventories</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Clock className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">Quick Turnaround</h4>
                      <p className="text-gray-600 text-sm">Minimize vehicle downtime</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">Consistent Scheduling</h4>
                      <p className="text-gray-600 text-sm">Reliable service you can count on</p>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Have Questions?</h4>
                  <div className="mb-3 flex items-center">
                    <Phone className="h-5 w-5 text-blue-500 mr-2" />
                    <a href="tel:+12345678900" className="text-blue-600 hover:underline">
                      (234) 567-8900
                    </a>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-blue-500 mr-2" />
                    <a href="mailto:dealerships@jpautodetailing.com" className="text-blue-600 hover:underline">
                      dealerships@jpautodetailing.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DealershipQuotePage;